import { AnimatePresence, motion } from 'motion/react';
import { useMemo } from 'react';
import StripeLogoType from '@/components/Stripe/LogoType';
import { Button, Loading } from '@music-vine/cadence/ui';
import { OverlayDescription } from './OverlayDescription';
import { OverlaySubtitle } from './OverlaySubtitle';
import type { StripeOverlayVariant } from '.';
export interface OverlayProps {
  customText?: {
    initial?: string;
    pending?: string;
    error?: string;
    errorSubtext?: string;
  };
  onDismiss?: () => void;
  show?: boolean;
  variant?: StripeOverlayVariant;
}
export const Overlay = ({
  show,
  variant,
  onDismiss,
  customText
}: OverlayProps) => {
  const descriptionComponent = useMemo(() => {
    if (variant === 'initial') {
      return <OverlayDescription key="initial-description">
          {customText?.initial || 'Redirecting you to Stripe to complete your payment...'}
        </OverlayDescription>;
    }
    if (variant === 'pending') {
      return <OverlayDescription key="pending-description">
          <div className="flex flex-col items-center justify-center gap-6">
            {customText?.pending || "We're confirming your payment..."}
            <div className="flex items-center justify-center">
              <Loading visible />
            </div>
          </div>
        </OverlayDescription>;
    }
    if (variant === 'error') {
      return <OverlayDescription key="error-description" variant="error">
          <div className="flex flex-col items-center justify-center gap-6 text-pretty">
            {customText?.error || 'We are unable to confirm payment at this time. Your subscription will update automatically.'}
            <br />
            <small className="italic opacity-50">
              {customText?.errorSubtext || 'If your subscription has still not processed within the next hour please contact support.'}
            </small>
            <div className="flex items-center justify-center">
              <Button onClick={onDismiss} size="sm" variant="subtle">
                Dismiss
              </Button>
            </div>
          </div>
        </OverlayDescription>;
    }
    return null;
  }, [onDismiss, variant, customText]);
  return <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-component="Overlay" data-sentry-source-file="Overlay.tsx">
      {show ? <motion.div animate={{
      opacity: 1
    }} className="fixed inset-0 z-[10000] flex h-screen w-screen flex-col items-center justify-center gap-6 bg-gray-950/90 font-normal" exit={{
      opacity: 0
    }} initial={{
      opacity: 0
    }} key="stripe-overlay" onClick={e => {
      e.preventDefault();
      e.stopPropagation();
    }} onDoubleClick={e => {
      e.preventDefault();
      e.stopPropagation();
    }} transition={{
      duration: 0.3
    }}>
          <motion.div className="flex flex-col items-center justify-center gap-4" key="base-elements">
            <StripeLogoType className="h-12 w-full" lettersFill="#FFFFFF" />
            <OverlaySubtitle />
          </motion.div>

          <AnimatePresence mode="wait">{descriptionComponent}</AnimatePresence>
        </motion.div> : null}
    </AnimatePresence>;
};